import { PLACEHOLDER_VALUE } from '@/constants';

export const emptyRow = {
  study_name: PLACEHOLDER_VALUE,
  org_name: PLACEHOLDER_VALUE,
  is_control: false,
  sample_type: '',
};

// 2-plex numbering; reachable only from the fallback coordinate path below.
export const generateWellPosition = (i: number) => Math.ceil((i - 1) / 2 + 1);

/** Where customer samples sit on the plate: `plexity` demux indices per well, first well A`firstCustomerWellIndex`. */
export type CustomerPlateLayout = { plexity: number; firstCustomerWellIndex: number };

type ControlCoordinate = { well_position: string; demux_index: number };

const wellIndex = (well: string): number => Number.parseInt(well.replace(/^\D+/, ''), 10);

// Customers are multiplexed as deeply as the controls and start one well past the *last* control
// well (so a non-contiguous control block leaves its gaps unused), derived from
// `/assays/run_configs` so the grid stays correct when an assay changes.
export function deriveCustomerPlateLayout(controls?: ControlCoordinate[] | null): CustomerPlateLayout | undefined {
  if (!controls || controls.length === 0) return undefined;
  const plexity = Math.max(...controls.map(c => c.demux_index));
  const lastControlWell = Math.max(...controls.map(c => wellIndex(c.well_position)));
  if (!Number.isInteger(plexity) || plexity < 1 || !Number.isInteger(lastControlWell)) return undefined;
  return { plexity, firstCustomerWellIndex: lastControlWell + 1 };
}

const customerCoords = (i: number, { plexity, firstCustomerWellIndex }: CustomerPlateLayout) => ({
  demux_index: (i % plexity) + 1,
  well_position: `A${firstCustomerWellIndex + Math.floor(i / plexity)}`,
});

// Applies before an assay is picked, or when its control layout is unusable. Starts customers at
// A1, which collides with every assay whose controls occupy the low wells.
const fallbackCoords = (i: number, n: number) => {
  if (n > 24) return { demux_index: (i % 3) + 1, well_position: `A${Math.floor(i / 3) + 1}` };
  if (n > 12) return { demux_index: (i % 2) + 1, well_position: `A${generateWellPosition(i)}` };
  return { demux_index: 1, well_position: `A${i + 1}` };
};

/** Empty *customer* rows only — control rows come from the assay's own layout, not from here. */
export function createEmptyRows(count?: number, layout?: CustomerPlateLayout) {
  const n = count ?? 12;
  return Array.from({ length: n }, (_, i) => ({
    ...emptyRow,
    ...(layout ? customerCoords(i, layout) : fallbackCoords(i, n)),
    name: `Sample ${i + 1}`,
  }));
}

/**
 * Helper function to convert a string to camelCase
 * @param str - The string to convert
 * @returns The camelCase version of the string
 * @example toCamelCase('Name') // 'name'
 * @example toCamelCase('wellPosition') // 'wellPosition'
 */
export const toCamelCase = (str: string): string => str.charAt(0).toLowerCase() + str.slice(1);
